import type { App, Environment, Platform } from '@hangar/shared';
import { FormField, fieldA11y } from '@/components/FormField';
import { OptionSelect } from '@/components/OptionSelect';
import { needsApp, needsEnvironment, needsPlatform, type ScopeKey } from './envHelpers';
import type { EnvVarFormApi } from './useEnvVarForm';

const PLATFORM_OPTIONS = [
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
];

const SCOPE_KEY_OPTIONS = [
  { value: 'org', label: 'Organization' },
  { value: 'project', label: 'Project · all environments' },
  { value: 'project-env', label: 'Project + environment' },
  { value: 'app', label: 'App · all environments' },
  { value: 'app-env', label: 'App + environment' },
  { value: 'platform', label: 'App + environment + platform' },
];

interface ScopeProps {
  form: EnvVarFormApi;
  apps: App[] | undefined;
  environments: Environment[];
}

function AppSelect({ form, apps }: Pick<ScopeProps, 'form' | 'apps'>) {
  return (
    <FormField id="env-var-app" label="App" required error={form.errorFor('app')}>
      <OptionSelect
        data-testid="env-var-app"
        value={form.values.appSlug}
        onValueChange={(appSlug) => form.patch({ appSlug })}
        placeholder="Pick"
        options={(apps ?? []).map((a) => ({ value: a.slug, label: a.slug }))}
        className="font-mono sm:text-[13px]"
        contentClassName="font-mono"
        {...fieldA11y('env-var-app', form.errorFor('app'))}
      />
    </FormField>
  );
}

function EnvironmentSelect({ form, environments }: Pick<ScopeProps, 'form' | 'environments'>) {
  return (
    <FormField id="env-var-environment" label="Environment" required error={form.errorFor('environment')}>
      <OptionSelect
        data-testid="env-var-environment"
        value={form.values.environment}
        onValueChange={(environment) => form.patch({ environment })}
        placeholder="Pick"
        options={environments.map((env) => ({ value: env.slug, label: env.name }))}
        {...fieldA11y('env-var-environment', form.errorFor('environment'))}
      />
    </FormField>
  );
}

function PlatformSelect({ form }: { form: EnvVarFormApi }) {
  return (
    <FormField id="env-var-platform" label="Platform" required error={form.errorFor('platform')}>
      <OptionSelect
        data-testid="env-var-platform"
        value={form.values.platform}
        onValueChange={(platform) => form.patch({ platform: platform as Platform })}
        placeholder="Pick"
        options={PLATFORM_OPTIONS}
        {...fieldA11y('env-var-platform', form.errorFor('platform'))}
      />
    </FormField>
  );
}

/** Scope select plus the app / environment / platform pickers that scope needs (new variables only). */
export function EnvVarScopeFields({ form, apps, environments }: ScopeProps) {
  const { scopeKey } = form.values;
  return (
    <fieldset className="flex flex-col gap-3 rounded-lg border bg-secondary/40 p-3">
      <legend className="px-1 text-xs font-semibold uppercase tracking-[0.08em] text-subtle">Scope</legend>
      <OptionSelect data-testid="env-var-scope" aria-label="Scope" value={scopeKey} onValueChange={(v) => form.patch({ scopeKey: v as ScopeKey })} options={SCOPE_KEY_OPTIONS} />
      <div className="grid gap-3 sm:grid-cols-3">
        {needsApp(scopeKey) ? <AppSelect form={form} apps={apps} /> : null}
        {needsEnvironment(scopeKey) ? <EnvironmentSelect form={form} environments={environments} /> : null}
        {needsPlatform(scopeKey) ? <PlatformSelect form={form} /> : null}
      </div>
    </fieldset>
  );
}
